/**
 * Theme Migration Utility
 * Handles migration from old theme localStorage keys to new ThemeService format
 * Ensures consistent theme management across the application
 */

export class ThemeMigrationUtil {
  private static readonly OLD_THEME_KEY = 'theme';
  private static readonly NEW_THEME_KEY = 'tibss_theme';
  private static readonly DEFAULT_THEME = 'light';

  /**
   * Migrate theme preferences from old localStorage format to new format
   * This ensures existing users don't lose their theme preferences
   */
  static migrateThemePreferences(): void {
    try {
      // Check if new theme key already exists
      const newTheme = localStorage.getItem(this.NEW_THEME_KEY);
      
      if (!newTheme) {
        // Check for old theme key
        const oldTheme = localStorage.getItem(this.OLD_THEME_KEY);
        
        if (oldTheme) {
          // Validate old theme value
          const validThemes = ['light', 'dark', 'auto'];
          const themeToMigrate = validThemes.includes(oldTheme) ? oldTheme : this.DEFAULT_THEME;
          
          // Migrate to new key
          localStorage.setItem(this.NEW_THEME_KEY, themeToMigrate);
          
          console.log(`✅ Theme migrated: ${oldTheme} -> ${themeToMigrate}`);
        } else {
          // No theme preference exists, set default
          localStorage.setItem(this.NEW_THEME_KEY, this.DEFAULT_THEME);
          console.log(`✅ Default theme set: ${this.DEFAULT_THEME}`);
        }
      }

      // Clean up old theme key to prevent conflicts
      if (localStorage.getItem(this.OLD_THEME_KEY)) {
        localStorage.removeItem(this.OLD_THEME_KEY);
        console.log('✅ Old theme key cleaned up');
      }

      // Clean up any other potential conflicting theme keys
      this.cleanupConflictingThemeKeys();
      
    } catch (error) {
      console.warn('Failed to migrate theme preferences:', error);
      // Fallback: ensure default theme is set
      localStorage.setItem(this.NEW_THEME_KEY, this.DEFAULT_THEME);
    }
  }

  /**
   * Clean up any other potential conflicting theme-related localStorage keys
   */
  private static cleanupConflictingThemeKeys(): void {
    const potentialConflictingKeys = [
      'darkMode',
      'themeMode',
      'ui-theme',
      'app-theme',
      'user-theme'
    ];

    potentialConflictingKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`✅ Cleaned up conflicting theme key: ${key}`);
      }
    });
  }

  /**
   * Force reset theme to light mode
   * Useful for debugging or user preference reset
   */
  static resetToLightTheme(): void {
    try {
      localStorage.setItem(this.NEW_THEME_KEY, 'light');
      
      // Remove dark class from document if it exists
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
      
      console.log('✅ Theme reset to light mode');
    } catch (error) {
      console.warn('Failed to reset theme:', error);
    }
  }

  /**
   * Get current theme preference with fallback
   */
  static getCurrentTheme(): string {
    try {
      return localStorage.getItem(this.NEW_THEME_KEY) || this.DEFAULT_THEME;
    } catch {
      return this.DEFAULT_THEME;
    }
  }

  /**
   * Check if user has any theme preference set
   */
  static hasThemePreference(): boolean {
    try {
      return !!localStorage.getItem(this.NEW_THEME_KEY);
    } catch {
      return false;
    }
  }
}
